import { relationFieldToRows } from './jenisPengukuranPerlengkapan';
import {
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  strapiFetchParameterUjisByDocumentIds,
} from './strapiCrm';
import type { DynamicEntityRow } from './strapiDynamicEntity';
import { STRAPI_URL, strapiAuthHeaders } from './strapi';

export const PERALATAN_TO_PARAMETER_UJI_API = 'peralatan-to-parameter-ujis';

/** Section checkbox pada form checklist kesiapan alat yang di-mapping per parameter uji. */
export const PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS = [
  'tujuan_pengambilan',
  'sni_methods',
  'metode_sampling',
  'larutan_penjerap',
  'peralatan_pengambilan_contoh',
  'peralatan_pengukuran_lapangan',
  'peralatan_pendukung',
  'peralatan_k3',
  'cara_pengambilan_contoh',
  'pengendalian_mutu_lapangan',
  'pengamanan_transportasi_contoh',
  'administrasi_contoh',
  'pengawetan',
] as const;

export type PeralatanToParameterUjiRelationField =
  (typeof PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS)[number];

export type PeralatanToParameterUjiRelations = Record<
  PeralatanToParameterUjiRelationField,
  DynamicEntityRow[]
>;

export type PeralatanToParameterUjiFields = PeralatanToParameterUjiRelations & {
  jumlah_wadah_contoh_uji_air: string;
  jumlah_wadah_contoh_uji_padatan: string;
};

export type PeralatanToParameterUjiDraft = PeralatanToParameterUjiFields & {
  parameter_uji: DynamicEntityRow | null;
};

export type PeralatanToParameterUjiMapping = PeralatanToParameterUjiFields & {
  documentId: string;
  parameterUjiDocumentId: string;
  /**
   * DocumentId parameter uji yang dicakup baris ini.
   * Termasuk duplikat master dengan nama dan simbol yang sama (mis. dua baris "pH").
   */
  appliesToParameterDocumentIds: string[];
};

/** Slice checklist yang bisa diisi dari master mapping. */
export type ParameterChecklistMappingSlice = PeralatanToParameterUjiFields;

function asRecord(v: unknown): Record<string, unknown> | null {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) return null;
  return v as Record<string, unknown>;
}

function emptyRelations(): PeralatanToParameterUjiRelations {
  return {
    tujuan_pengambilan: [],
    sni_methods: [],
    metode_sampling: [],
    larutan_penjerap: [],
    peralatan_pengambilan_contoh: [],
    peralatan_pengukuran_lapangan: [],
    peralatan_pendukung: [],
    peralatan_k3: [],
    cara_pengambilan_contoh: [],
    pengendalian_mutu_lapangan: [],
    pengamanan_transportasi_contoh: [],
    administrasi_contoh: [],
    pengawetan: [],
  };
}

export function emptyPeralatanToParameterUjiDraft(): PeralatanToParameterUjiDraft {
  return {
    parameter_uji: null,
    ...emptyRelations(),
    jumlah_wadah_contoh_uji_air: '',
    jumlah_wadah_contoh_uji_padatan: '',
  };
}

function unionRows(groups: DynamicEntityRow[][]): DynamicEntityRow[] {
  const seen = new Set<string>();
  const out: DynamicEntityRow[] = [];
  for (const rows of groups) {
    for (const row of rows) {
      const documentId = String(row.documentId ?? '').trim();
      if (!documentId || seen.has(documentId)) continue;
      seen.add(documentId);
      out.push(row);
    }
  }
  return out;
}

function setMany(rows: DynamicEntityRow[]): { set: string[] } {
  return {
    set: rows.map((row) => String(row.documentId ?? '').trim()).filter(Boolean),
  };
}

export function draftFromPeralatanToParameterUjiRow(row: DynamicEntityRow): PeralatanToParameterUjiDraft {
  const draft = emptyPeralatanToParameterUjiDraft();
  draft.parameter_uji = relationFieldToRows(row.parameter_uji)[0] ?? null;
  for (const field of PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS) {
    draft[field] = relationFieldToRows(row[field]);
  }
  draft.jumlah_wadah_contoh_uji_air = String(row.jumlah_wadah_contoh_uji_air ?? '');
  draft.jumlah_wadah_contoh_uji_padatan = String(row.jumlah_wadah_contoh_uji_padatan ?? '');
  return draft;
}

export function buildPeralatanToParameterUjiWritePayload(
  draft: PeralatanToParameterUjiDraft,
): Record<string, unknown> {
  const parameterId = String(draft.parameter_uji?.documentId ?? '').trim();
  if (!parameterId) throw new Error('Parameter uji wajib dipilih.');
  const payload: Record<string, unknown> = {
    parameter_uji: { set: [parameterId] },
    jumlah_wadah_contoh_uji_air: draft.jumlah_wadah_contoh_uji_air.trim(),
    jumlah_wadah_contoh_uji_padatan: draft.jumlah_wadah_contoh_uji_padatan.trim(),
  };
  for (const field of PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS) {
    payload[field] = setMany(draft[field]);
  }
  return payload;
}

function flattenApiRow(raw: unknown): DynamicEntityRow | null {
  const rec = asRecord(raw);
  if (!rec) return null;
  const attrs = asRecord(rec.attributes);
  const documentId = String(rec.documentId ?? attrs?.documentId ?? '').trim();
  if (!documentId) return null;
  return (attrs ? { ...attrs, documentId } : { ...rec, documentId }) as DynamicEntityRow;
}

function parameterIdentityKey(nama: string | null | undefined, simbol: string | null | undefined): string | null {
  const name = String(nama ?? '').trim();
  const symbol = String(simbol ?? '').trim();
  if (!name || !symbol) return null;
  return `${name}\0${symbol}`;
}

function rowToMapping(
  row: DynamicEntityRow,
  appliesToByParameterId: Map<string, string[]>,
): PeralatanToParameterUjiMapping | null {
  const parameter = relationFieldToRows(row.parameter_uji)[0];
  const parameterUjiDocumentId = String(parameter?.documentId ?? '').trim();
  if (!parameterUjiDocumentId) return null;
  const relations = emptyRelations();
  for (const field of PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS) {
    relations[field] = relationFieldToRows(row[field]);
  }
  const appliesToParameterDocumentIds = [
    ...new Set([parameterUjiDocumentId, ...(appliesToByParameterId.get(parameterUjiDocumentId) ?? [])]),
  ];
  return {
    documentId: row.documentId,
    parameterUjiDocumentId,
    appliesToParameterDocumentIds,
    ...relations,
    jumlah_wadah_contoh_uji_air: String(row.jumlah_wadah_contoh_uji_air ?? ''),
    jumlah_wadah_contoh_uji_padatan: String(row.jumlah_wadah_contoh_uji_padatan ?? ''),
  };
}

/**
 * Master parameter uji punya baris duplikat (nama + simbol sama, documentId beda).
 * Mapping yang disimpan di salah satu baris harus ikut terbaca untuk baris kembarannya.
 */
async function parameterIdsSharingIdentity(jwt: string, parameterUjiDocumentIds: string[]): Promise<Map<string, string[]>> {
  const requested = [...new Set(parameterUjiDocumentIds.map((id) => id.trim()).filter(Boolean))];
  const appliesTo = new Map<string, string[]>();
  for (const id of requested) appliesTo.set(id, [id]);
  if (!requested.length) return appliesTo;

  const known = await strapiFetchParameterUjisByDocumentIds(jwt, requested);
  const requestedByIdentity = new Map<string, string[]>();
  for (const id of requested) {
    const info = known.get(id);
    const key = parameterIdentityKey(info?.nama_parameter, info?.simbol);
    if (!key) continue;
    const bucket = requestedByIdentity.get(key) ?? [];
    bucket.push(id);
    requestedByIdentity.set(key, bucket);
  }
  const identities = [...requestedByIdentity.entries()];
  const chunkSize = 20;
  for (let i = 0; i < identities.length; i += chunkSize) {
    const chunk = identities.slice(i, i + chunkSize);
    const params = new URLSearchParams({
      'pagination[pageSize]': '100',
      'pagination[page]': '1',
      'fields[0]': 'documentId',
      'fields[1]': 'nama_parameter',
      'fields[2]': 'simbol',
    });
    chunk.forEach(([, ids], idx) => {
      const info = known.get(ids[0]!);
      params.append(`filters[$or][${idx}][$and][0][nama_parameter][$eq]`, String(info?.nama_parameter ?? '').trim());
      params.append(`filters[$or][${idx}][$and][1][simbol][$eq]`, String(info?.simbol ?? '').trim());
    });
    const res = await fetch(`${STRAPI_URL}/api/parameter-ujis?${params}`, { headers: strapiAuthHeaders(jwt) });
    const body = (await res.json().catch(() => ({}))) as { data?: unknown[]; error?: { message?: string } };
    if (!res.ok) {
      throw new Error(body.error?.message ?? `Gagal memuat parameter uji kembar (${res.status})`);
    }
    for (const raw of Array.isArray(body.data) ? body.data : []) {
      const flat = flattenApiRow(raw);
      if (!flat) continue;
      const documentId = String(flat.documentId ?? '').trim();
      const key = parameterIdentityKey(
        typeof flat.nama_parameter === 'string' ? flat.nama_parameter : null,
        typeof flat.simbol === 'string' ? flat.simbol : null,
      );
      if (!documentId || !key) continue;
      const requestedIds = requestedByIdentity.get(key) ?? [];
      const current = appliesTo.get(documentId) ?? [];
      appliesTo.set(documentId, [...new Set([...current, ...requestedIds, documentId])]);
    }
  }
  return appliesTo;
}

async function fetchMappings(
  jwt: string,
  parameterUjiDocumentIds: string[],
): Promise<PeralatanToParameterUjiMapping[]> {
  const ids = [...new Set(parameterUjiDocumentIds.map((id) => id.trim()).filter(Boolean))];
  if (!ids.length) return [];
  const appliesToByParameterId = await parameterIdsSharingIdentity(jwt, ids);
  const lookupIds = [...appliesToByParameterId.keys()];
  const out: PeralatanToParameterUjiMapping[] = [];
  const chunkSize = 20;
  for (let i = 0; i < lookupIds.length; i += chunkSize) {
    const chunk = lookupIds.slice(i, i + chunkSize);
    const params = new URLSearchParams({
      'pagination[pageSize]': '100',
      'pagination[page]': '1',
      sort: 'updatedAt:desc',
      populate: '*',
    });
    chunk.forEach((id, idx) => {
      params.append(`filters[$or][${idx}][parameter_uji][documentId][$eq]`, id);
    });
    const res = await fetch(
      `${STRAPI_URL}/api/${encodeURIComponent(PERALATAN_TO_PARAMETER_UJI_API)}?${params}`,
      { headers: strapiAuthHeaders(jwt) },
    );
    const body = (await res.json().catch(() => ({}))) as { data?: unknown[]; error?: { message?: string } };
    if (!res.ok) {
      throw new Error(body.error?.message ?? `Gagal memuat mapping peralatan × parameter uji (${res.status})`);
    }
    for (const raw of Array.isArray(body.data) ? body.data : []) {
      const flat = flattenApiRow(raw);
      const mapping = flat ? rowToMapping(flat, appliesToByParameterId) : null;
      if (mapping) out.push(mapping);
    }
  }
  return out;
}

export function mappingAppliesToParameterIds(
  mapping: PeralatanToParameterUjiMapping,
  parameterDocumentIds: readonly string[],
): boolean {
  const wanted = new Set(parameterDocumentIds.map((id) => id.trim()).filter(Boolean));
  return mapping.appliesToParameterDocumentIds.some((id) => wanted.has(id));
}

export async function strapiListPeralatanToParameterUjiByParameterIds(
  jwt: string,
  parameterUjiDocumentIds: string[],
): Promise<PeralatanToParameterUjiMapping[]> {
  return fetchMappings(jwt, parameterUjiDocumentIds);
}

export async function strapiFindPeralatanToParameterUjiDocumentId(
  jwt: string,
  parameterUjiDocumentId: string,
): Promise<string | null> {
  const rows = await fetchMappings(jwt, [parameterUjiDocumentId]);
  return rows[0]?.documentId ?? null;
}

export function parameterDocumentIdsFromSampleLines(
  lines: Array<{
    parameter_uji?: string | null;
    parameter_ujis?: Array<{ documentId?: string | null }> | null;
  }>,
): string[] {
  const set = new Set<string>();
  for (const line of lines) {
    for (const item of line.parameter_ujis ?? []) {
      const documentId = String(item.documentId ?? '').trim();
      if (documentId) set.add(documentId);
    }
    for (const token of parseStoredParameterUjiDocumentIds(line.parameter_uji ?? '')) {
      const id = token.trim();
      if (isLikelyStrapiDocumentId(id)) set.add(id);
    }
  }
  return [...set];
}

export function checklistParameterMappingSectionsEmpty(state: ParameterChecklistMappingSlice): boolean {
  for (const field of PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS) {
    if (state[field].length > 0) return false;
  }
  return !state.jumlah_wadah_contoh_uji_air.trim() && !state.jumlah_wadah_contoh_uji_padatan.trim();
}

function joinedWadah(mappings: PeralatanToParameterUjiMapping[], field: 'jumlah_wadah_contoh_uji_air' | 'jumlah_wadah_contoh_uji_padatan'): string {
  return [...new Set(mappings.map((row) => row[field].trim()).filter(Boolean))].join(', ');
}

/** Isi section yang masih kosong. Section yang sudah terisi tidak diubah. */
export function applyParameterUjiMappingsToEmptySections<T extends ParameterChecklistMappingSlice>(
  state: T,
  mappings: PeralatanToParameterUjiMapping[],
): T {
  if (!mappings.length) return state;
  const next: T = { ...state };
  let changed = false;
  for (const field of PERALATAN_TO_PARAMETER_UJI_RELATION_FIELDS) {
    if (state[field].length > 0) continue;
    const merged = unionRows(mappings.map((row) => row[field]));
    if (!merged.length) continue;
    next[field] = merged;
    changed = true;
  }
  if (!state.jumlah_wadah_contoh_uji_air.trim()) {
    const value = joinedWadah(mappings, 'jumlah_wadah_contoh_uji_air');
    if (value) {
      next.jumlah_wadah_contoh_uji_air = value;
      changed = true;
    }
  }
  if (!state.jumlah_wadah_contoh_uji_padatan.trim()) {
    const value = joinedWadah(mappings, 'jumlah_wadah_contoh_uji_padatan');
    if (value) {
      next.jumlah_wadah_contoh_uji_padatan = value;
      changed = true;
    }
  }
  return changed ? next : state;
}

/** Isi seluruh mapping hanya jika checklist belum punya satupun section mapping. */
export function applyParameterUjiMappingsToEmptyChecklist<T extends ParameterChecklistMappingSlice>(
  state: T,
  mappings: PeralatanToParameterUjiMapping[],
): T {
  if (!checklistParameterMappingSectionsEmpty(state)) return state;
  return applyParameterUjiMappingsToEmptySections(state, mappings);
}
